import axios from 'axios';

axios.defaults.withCredentials = true;

const api = axios.create({
  headers: {
    'Content-Type': 'application/json',
  },
  baseURL: import.meta.env.VITE_API_BASE_URL,
});

const pendingGets = new Map();

const deduplicatedGet = async (url) => {
  if (pendingGets.has(url)) {
    return pendingGets.get(url);
  }

  const promise = api.get(url).finally(() => {
    pendingGets.delete(url);
  });

  pendingGets.set(url, promise);

  return promise;
};

export const getUser = async () => {
  try {
    const { data } = await api.get('/auth/user');
    return data;
  } catch (error) {
    console.error('Error getting logged in user:', error);
    throw error;
  }
};

export const login = async (email) => {
  try {
    const { data } = await api.post('/auth/login', { email });
    return data;
  } catch (error) {
    console.error('Error logging in:', error);
    throw error;
  }
};

export const verifyOTP = async (email, otp) => {
  try {
    const { data } = await api.post('/auth/otp/verify', { otp, email });
    return data;
  } catch (error) {
    console.error('Error verifying otp:', error);
    throw error;
  }
};

export const getAppleToken = async () => {
  try {
    const { data } = await api.post('/apple/token');
    return data;
  } catch (error) {
    console.error('Error getting apple token:', error);
    throw error;
  }
};

export const connectApple = async (musicUserToken) => {
  try {
    const { data } = await api.post('/apple/auth/connect', { musicUserToken });
    return data;
  } catch (error) {
    console.error('Error connecting Apple Music:', error);
    throw error;
  }
};

export const getAppleStatus = async () => {
  try {
    const { data } = await deduplicatedGet('/apple/auth/status');
    return data;
  } catch (error) {
    console.error('Error getting Apple status:', error);
    throw error;
  }
};

export const disconnectAppleAccount = async (accountId) => {
  try {
    const { data } = await api.post('/apple/auth/disconnect', { accountId });
    return data;
  } catch (error) {
    console.error('Error disconnecting Apple account:', error);
    throw error;
  }
};

export const exchangeSpotifyCode = async (code) => {
  try {
    const { data } = await api.post('/spotify/auth/exchange', { code });
    return data;
  } catch (error) {
    console.error('Error exchanging Spotify code:', error);
    throw error;
  }
};

export const getSpotifyStatus = async () => {
  try {
    const { data } = await deduplicatedGet('/spotify/auth/status');
    return data;
  } catch (error) {
    console.error('Error getting Spotify status:', error);
    throw error;
  }
};

export const disconnectSpotifyAccount = async (accountId) => {
  try {
    const { data } = await api.post('/spotify/auth/disconnect', {
      accountId,
    });
    return data;
  } catch (error) {
    console.error('Error disconnecting Spotify account:', error);
    throw error;
  }
};

export const exchangeYouTubeCode = async (code) => {
  try {
    const { data } = await api.post('/youtube/auth/exchange', { code });
    return data;
  } catch (error) {
    console.error('Error exchanging YouTube code:', error);
    throw error;
  }
};

export const getYouTubeStatus = async () => {
  try {
    const { data } = await deduplicatedGet('/youtube/auth/status');
    return data;
  } catch (error) {
    console.error('Error getting YouTube status:', error);
    throw error;
  }
};

export const disconnectYouTubeAccount = async (accountId) => {
  try {
    const { data } = await api.post('/youtube/auth/disconnect', {
      accountId,
    });
    return data;
  } catch (error) {
    console.error('Error disconnecting YouTube account:', error);
    throw error;
  }
};

export const convert = async ({
  mode,
  type,
  source,
  target,
  isPrivate,
  sourceIds,
  sourceUrl,
  sourceAuthId,
  targetAuthId,
  sourceProfileUrl,
}) => {
  try {
    const { data } = await api.post(
      '/convert',
      {
        mode,
        type,
        source,
        target,
        isPrivate,
        sourceIds,
        sourceUrl,
        sourceProfileUrl,
      },
      {
        headers: {
          'X-Source-Auth-Id': sourceAuthId,
          'X-Target-Auth-Id': targetAuthId,
        },
      }
    );
    return data;
  } catch (error) {
    console.error('Error converting:', error);
    throw error;
  }
};

export const getAccountPlaylists = async (platform, authId) => {
  try {
    const { data } = await api.get(`/playlists/account/${platform}`, {
      headers: { 'X-Auth-Id': authId },
    });

    return data;
  } catch (error) {
    console.error('Error getting account playlists:', error);
    throw error;
  }
};

export const getProfilePlaylists = async (platform, url) => {
  try {
    const { data } = await api.get(`playlists/profile/${platform}`, {
      params: { url },
    });

    return data;
  } catch (error) {
    console.error('Error getting profile playlists:', error);
    throw error;
  }
};

export const getPriorConversions = async ({
  source,
  target,
  targetAuthId,
  sourceAuthId,
}) => {
  try {
    const headers = { 'X-Target-Auth-Id': targetAuthId };

    if (sourceAuthId) {
      headers['X-Source-Auth-Id'] = sourceAuthId;
    }

    const { data } = await api.get('/conversions/priors', {
      params: { source, target },
      headers,
    });

    return data;
  } catch (error) {
    console.error('Error getting prior conversions:', error);
    throw error;
  }
};

export const getProgress = async (conversionId) => {
  try {
    const { data } = await api.get(`conversions/${conversionId}/progress`);

    return data;
  } catch (error) {
    console.error('Error getting conversion progress:', error);
    throw error;
  }
};

export const retryConversion = async (
  conversionId,
  { sourceAuthId, targetAuthId, force }
) => {
  try {
    const headers = {
      'X-Source-Auth-Id': sourceAuthId,
      'X-Target-Auth-Id': targetAuthId,
    };

    if (force) {
      headers['X-Force-Retry'] = 'true';
    }

    const { data } = await api.post(
      `/conversions/${conversionId}/retry`,
      {},
      { headers }
    );
    return data;
  } catch (error) {
    console.error('Error retrying conversion:', error);
    throw error;
  }
};

export const getConversionHistory = async (
  page = 1,
  limit = 10,
  filters = {}
) => {
  try {
    const { data } = await api.get('/conversions/history', {
      params: { page, limit, ...filters },
    });

    return data;
  } catch (error) {
    console.error('Error getting conversion history:', error);
    throw error;
  }
};

export const getConversionDetails = async (conversionId, sourceAuthId) => {
  try {
    const headers = sourceAuthId ? { 'X-Source-Auth-Id': sourceAuthId } : {};

    const { data } = await api.get(`/conversions/${conversionId}/details`, {
      headers,
    });

    return data;
  } catch (error) {
    console.error('Error getting conversion details:', error);
    throw error;
  }
};
